import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';
import { createBreadcrumbSchema, createImageGallerySchema } from '../lib/schema';
import { pagesData } from '../data';
import { X, Maximize2, Phone, Mail, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../lib/utils';
import { fetchCollection } from '../lib/cms';

const getOptimizedImageUrl = (src, width = 800) => {
  if (src && src.startsWith('https://assets.auxodev.dk/')) {
    if (src.includes('/cdn-cgi/image/')) return src;
    return src.replace('https://assets.auxodev.dk/', `https://assets.auxodev.dk/cdn-cgi/image/width=${width},quality=80,format=auto/`);
  }
  return src;
};

export const mapCategoryToService = (category, alt = '', src = '') => {
  const catLower = category.toLowerCase();
  const altLower = alt.toLowerCase();
  const srcLower = src.toLowerCase();

  // 0. Ny indkørsel (Specific files or category)
  if (
    category === 'Ny indkørsel' ||
    catLower.includes('ny indkørsel') ||
    srcLower.includes('ind.jpg') ||
    srcLower.includes('ind2.jpg') ||
    srcLower.includes('ind3.jpg')
  ) {
    return 'Ny indkørsel';
  }

  // 1. Træfældning og haveservice
  if (
    altLower.includes('træ') || 
    altLower.includes('beskæring') || 
    altLower.includes('topkapning') || 
    altLower.includes('fældning') || 
    altLower.includes('chainsaw') || 
    altLower.includes('saw') || 
    altLower.includes('lumberjack')
  ) {
    return 'Træfældning og haveservice';
  }

  // 2. Støbning af beton
  if (
    altLower.includes('støbning') || 
    altLower.includes('støbt') || 
    altLower.includes('beton') || 
    altLower.includes('trappe') || 
    altLower.includes('sokkel') ||
    altLower.includes('overfladevand')
  ) {
    return 'Støbning af beton';
  }

  // 3. Anlæg og belægning
  if (
    catLower.includes('anlæg og belægning') || 
    altLower.includes('belægning') || 
    altLower.includes('flise') || 
    altLower.includes('fliser') || 
    altLower.includes('brolægning') || 
    altLower.includes('brolægger') || 
    altLower.includes('terrasse') || 
    altLower.includes('skraldespandsplads')
  ) {
    return 'Anlæg og belægning';
  }

  // 4. Haveservice
  if (
    catLower.includes('haveservice') || 
    altLower.includes('hæk') || 
    altLower.includes('bæk') || 
    altLower.includes('pileurt') || 
    altLower.includes('have') || 
    altLower.includes('græs') || 
    altLower.includes('skråning') ||
    altLower.includes('trampolin')
  ) {
    return 'Haveservice';
  }

  // 5. Ny indkørsel
  if (
    altLower.includes('indkørsel') || 
    altLower.includes('indgangsparti')
  ) {
    return 'Ny indkørsel';
  }

  // 6. SpildevandTV
  if (
    altLower.includes('tv-inspektion') || 
    altLower.includes('tv inspektion') || 
    altLower.includes('spildevands-tv') || 
    altLower.includes('spildevandtv') || 
    altLower.includes('spildevand tv') || 
    altLower.includes('spildevandsbrønd')
  ) {
    return 'SpildevandTV';
  }

  // 7. Kloakmester arbejde
  if (
    category === 'Kloakmesterarbejde' ||
    catLower.includes('kloakmesterarbejde') ||
    altLower.includes('tørledninger') ||
    altLower.includes('regnvandsstik') ||
    altLower.includes('regnvandskobling') ||
    src.includes('20250903-074412358') ||
    src.includes('20250903-074421600') ||
    src.includes('20250903-074911842') ||
    src.includes('20260415-073300097') ||
    src.includes('20260415-082022658') ||
    src.includes('20260416-113502892') ||
    src.includes('20260329-111904381') ||
    src.includes('20260401-050929240') ||
    src.includes('20260329-070521889') ||
    src.includes('20260328-121632724') ||
    src.includes('20260328-104437576') ||
    src.includes('20260328-104433376')
  ) {
    return 'Kloakmesterarbejde';
  }

  // 8. Inspektion
  if (
    catLower.includes('oprensning, spulning') || 
    altLower.includes('inspektion') || 
    altLower.includes('fejlfinding') || 
    altLower.includes('kontrol') || 
    altLower.includes('sporing') || 
    altLower.includes('søgning') || 
    altLower.includes('tankattest') || 
    altLower.includes('spulning') || 
    altLower.includes('oprensning af tank') || 
    altLower.includes('kloakspulning') || 
    altLower.includes('bundfældningstank')
  ) {
    return 'Inspektion';
  }

  // 9. Kloak separering
  if (
    catLower.includes('kloakseparering') || 
    catLower.includes('separering alken') || 
    altLower.includes('separering') || 
    altLower.includes('regnvand')
  ) {
    return 'Kloak separering';
  }

  // Default fallback
  return null;
};

export const allowedFilenames = [
  // Ny indkørsel
  'ind.jpg',
  'ind2.jpg',
  'ind3.jpg',

  // Haveservice
  '20250927-053948248-ios.jpg',
  '20250927-053950969-ios.jpg',
  '20250927-054001444-ios.jpg',
  'have.jpg',
  '20250826-084144736-ios.jpg',
  '20250826-084155999-ios.jpg',
  '20250826-084201876-ios.jpg',
  '20250826-115256381-ios.jpg',
  '20250826-115257775-ios.jpg',
  '20250830-093915103-ios.jpg',
  'trampolin.jpeg',
  '20250720-112449479-ios.jpg',
  '20250720-100634242-ios.jpeg',

  // SpildevandTV
  '20251128-184405469-ios.jpg',
  '20251128-184419941-ios.jpg',

  // Støbning af beton
  '20251126-094115508-ios.jpg',
  '20251126-094125115-ios.jpg',
  '20251126-112212802-ios.jpg',
  '20251126-112219151-ios.jpg',
  '20251126-112222605-ios.jpg',
  '20260305-063906252-ios.jpg',
  '20260305-104906667-ios.jpg',
  '20260305-063847465-ios.jpg',
  '20260305-063913414-ios.jpg',
  '20260309-140238873-ios-1.jpg',
  '20260309-140236239-ios.jpg',
  '20260309-064551539-ios-1.jpg',
  '20260309-064559088-ios.jpg',
  '20260309-064555066-ios-1.jpg',

  // Anlæg og belægning (de 19 specifikke)
  'bela-gning-2.jpeg',
  'bela-gning.jpeg',
  '20260522-094248782-ios_1.jpg',
  '20260522-094305297-ios_1.jpg',
  '20260522-094313377-ios_1.jpg',
  '20260522-094333612-ios_1.jpg',
  '20260324-130051882-ios.jpg',
  '20260324-130100816-ios.jpg',
  '20250823-110113708-ios.jpg',
  '20251023-085426295-ios.jpg',
  '20260527-122653119-ios_1.jpg',
  '20260527-122657675-ios_1.jpg',
  '20260527-122700304-ios_1.jpg',
  '20260527-122708965-ios_1.jpg',
  '20260527-122712413-ios_1.jpg',
  '20260527-122720509-ios_1.jpg',
  '20260527-122726285-ios_1.jpg',
  '20260527-122737475-ios_1.jpg',
  'content_3.jpg',

  // Kloakmesterarbejde (de 3 specifikke)
  '20250903-074412358-ios_1.jpg',
  '20250903-074421600-ios_1_1.jpg',
  '20250903-074421600-ios_1.jpg',
  '20250903-074911842-ios_1.jpg',
  '20260415-073300097-ios_2.jpg',
  '20260415-082022658-ios_2.jpg',
  '20260416-113502892-ios_2.jpg',
  '20260415-073300097-ios_3.jpg',
  '20260415-082022658-ios_3.jpg',
  '20260416-113502892-ios_3.jpg',
  '20260329-111904381-ios-1.jpg',
  '20260401-050929240-ios-1.jpg',
  '20260329-070521889-ios.jpg',
  '20260328-121632724-ios.jpg',
  '20260328-104437576-ios.jpg',
  '20260328-104433376-ios-1.jpg',

  // Træfældning og haveservice
  'beaaee35-3499-4378-81f6-c05b5114034e.jpg',
  '44ca76cf-b18c-43c3-839e-b06494853527.jpg',
  '377d9a9a-0543-4e0b-8aaa-151f691dbed4.jpg',
  'a1342fde-1896-497a-806b-51039f257f28.jpg'
];

export default function GalleryPage() {
  const page = pagesData.galleri;
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = searchParams.get('category') || 'Alle';
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const data = await fetchCollection('gallery_items');
        setItems(data);
      } catch (err) {
        console.error('Error loading gallery items:', err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  // Reset page when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filter]);

  if (!page) return <div className="py-40 text-center">Siden blev ikke fundet.</div>;

  if (loading) {
    return (
      <div className="py-40 text-center text-stone-600">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mb-4"></div>
        <p>Indlæser galleri...</p>
      </div>
    );
  }

  const mappedImages = items.map(item => {
    const categorySlug = item.terms?.['gallery-category']?.[0];
    const slugToLabel = {
      'belaegning': 'Belægning og Anlæg',
      'haveservice': 'Haveservice',
      'kloak': 'Kloak',
      'fundament': 'Beton og Fundament',
      'entreprenoerarbejde': 'Entreprenørarbejde',
      'traefaeldning-og-beskaering': 'Træfældning og beskæring',
      'stoettemur': 'Støttemur'
    };
    const categoryLabel = slugToLabel[categorySlug] || 'Diverse';
    return {
      src: item.image?.[0] || '',
      alt: item.title?.da || '',
      category: categoryLabel,
      slug: item.slug?.da || item.slug || ''
    };
  });

  // Sort mappedImages so that 'anlaeg1', 'anlaeg2', 'anlaeg3', 'anlaeg4' are sequential and come first
  const sortedMappedImages = [...mappedImages].sort((a, b) => {
    const altA = (a.alt || '').toLowerCase();
    const altB = (b.alt || '').toLowerCase();
    const isTvA = altA === 'tv-inspektion';
    const isTvB = altB === 'tv-inspektion';

    // TV-inspektion skal altid være helt til sidst og ved siden af hinanden
    if (isTvA && !isTvB) return 1;
    if (!isTvA && isTvB) return -1;
    if (isTvA && isTvB) {
      return (a.slug || '').localeCompare(b.slug || '');
    }

    // Kloakseparering skal også grupperes og ligge lige før TV-inspektion
    const isKloakSepA = altA === 'kloakseparering';
    const isKloakSepB = altB === 'kloakseparering';

    if (isKloakSepA && !isKloakSepB) {
      if (isTvB) return -1;
      return 1;
    }
    if (!isKloakSepA && isKloakSepB) {
      if (isTvA) return 1;
      return -1;
    }
    if (isKloakSepA && isKloakSepB) {
      return (a.slug || '').localeCompare(b.slug || '');
    }

    const slugA = a.slug || '';
    const slugB = b.slug || '';
    
    const isAnlaegA = slugA.startsWith('anlaeg');
    const isAnlaegB = slugB.startsWith('anlaeg');
    
    if (isAnlaegA && isAnlaegB) {
      return slugA.localeCompare(slugB); // anlaeg1, anlaeg2, anlaeg3, anlaeg4
    }
    if (isAnlaegA) return -1;
    if (isAnlaegB) return 1;
    
    const isBaekA = slugA.startsWith('opgravning-af-en-baek');
    const isBaekB = slugB.startsWith('opgravning-af-en-baek');

    if (isBaekA && isBaekB) {
      return slugA.localeCompare(slugB); // opgravning-af-en-baek, opgravning-af-en-baek1, opgravning-af-en-baek2, opgravning-af-en-baek3
    }
    if (isBaekA) return -1;
    if (isBaekB) return 1;

    const isStenA = slugA.startsWith('belaegningsten');
    const isStenB = slugB.startsWith('belaegningsten');

    if (isStenA && isStenB) {
      return slugA.localeCompare(slugB); // belaegningsten, belaegningsten1
    }
    if (isStenA) return -1;
    if (isStenB) return 1;

    const fiveNewBelaegningSlugs = [
      'etablering-af-ny-fliseterrasse-2',
      'professionel-fliselaegning-i-have',
      'flot-belaegningsarbejde-med-fliser',
      'nyanlagt-flisebelaegning-og-have',
      'faerdigt-flisearbejde-i-have'
    ];
    const isFiveNewBelaegningA = fiveNewBelaegningSlugs.includes(slugA);
    const isFiveNewBelaegningB = fiveNewBelaegningSlugs.includes(slugB);

    if (isFiveNewBelaegningA && isFiveNewBelaegningB) {
      return fiveNewBelaegningSlugs.indexOf(slugA) - fiveNewBelaegningSlugs.indexOf(slugB);
    }
    if (isFiveNewBelaegningA) return -1;
    if (isFiveNewBelaegningB) return 1;

    const latestBelaegningSlugs = [
      'professionel-belaegning-og-haveanlaeg',
      'flot-nyanlagt-flisebelaegning',
      'etablering-af-fliseterrasse-og-have',
      'faerdigt-flisearbejde-og-belaegning'
    ];
    const isLatestBelaegningA = latestBelaegningSlugs.includes(slugA);
    const isLatestBelaegningB = latestBelaegningSlugs.includes(slugB);

    if (isLatestBelaegningA && isLatestBelaegningB) {
      return latestBelaegningSlugs.indexOf(slugA) - latestBelaegningSlugs.indexOf(slugB);
    }
    if (isLatestBelaegningA) return -1;
    if (isLatestBelaegningB) return 1;

    const newBelaegningSlugs = [
      'professionel-flisebelaegning-i-indkoersel',
      'nyanlagt-fliseterrasse-og-gangsti',
      'praecisionsarbejde-med-herregaardssten',
      'faerdigt-belaegnings-og-anlaegsarbejde'
    ];
    const isNewBelaegningA = newBelaegningSlugs.includes(slugA);
    const isNewBelaegningB = newBelaegningSlugs.includes(slugB);

    if (isNewBelaegningA && isNewBelaegningB) {
      return newBelaegningSlugs.indexOf(slugA) - newBelaegningSlugs.indexOf(slugB);
    }
    if (isNewBelaegningA) return -1;
    if (isNewBelaegningB) return 1;

    return 0; // Keep original order for other items
  });

  const offeredServices = [
    'Kloakmesterarbejde',
    'Kloak separering',
    'SpildevandTV',
    'Anlæg og belægning',
    'Støbning af beton',
    'Træfældning og haveservice',
    'Haveservice',
    'Ny indkørsel'
  ];

  // Only show services that actually have images after mapping
  const categories = ['Alle', 'Belægning og Anlæg', 'Haveservice', 'Kloak', 'Beton og Fundament', 'Entreprenørarbejde', 'Støttemur'];
  const filteredItems = filter === 'Alle' ? sortedMappedImages : sortedMappedImages.filter(item => item.category === filter);

  // Pagination logic
  const ITEMS_PER_PAGE = 9;
  const totalPages = Math.ceil(filteredItems.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const currentItems = filteredItems.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const handleFilterChange = (cat) => {
    if (cat === 'Alle') {
      searchParams.delete('category');
    } else {
      searchParams.set('category', cat);
    }
    setSearchParams(searchParams);
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    const element = document.getElementById('gallery-section');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const breadcrumbSchema = createBreadcrumbSchema([
    { name: 'Forside', url: 'https://janhedegaard.dk/' },
    { name: 'Galleri', url: 'https://janhedegaard.dk/galleri' }
  ]);

  const imageGallerySchema = createImageGallerySchema({
    items: sortedMappedImages
  });
  const schemaList = [imageGallerySchema, breadcrumbSchema];

  return (
    <>
      <SEO 
        title={page.title} 
        description={page.meta.ogDescription}
        image={page.meta?.ogImage}
        schemaMarkup={schemaList}
      />

      {/* Hero Section */}
      <section className="relative min-h-[480px] py-24 md:py-28 flex items-center bg-stone-900">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src={getOptimizedImageUrl("https://assets.auxodev.dk/assets/6015610c-faa3-4021-bc7e-a866f30ca512/cfedd232-70ee-41da-a16f-a4d21d9510cb.jpg", 1600)} 
            alt="Gravemaskine i arbejde ved udgravning til kloak og fundament" 
            className="w-full h-full object-cover opacity-55"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-stone-950/70 via-stone-900/40 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full mt-16 pt-[40px]">
          <div className="mb-6">
            <Breadcrumbs items={[{ label: 'Galleri', href: '/galleri' }]} isDark={true} />
          </div>
          <h1 className="text-[1.8rem] md:text-6xl font-bold text-white drop-shadow-lg max-w-4xl leading-[1.15]">
            {page.headline}
          </h1>
          <p className="mt-6 text-lg md:text-xl text-stone-300 max-w-2xl drop-shadow">
            {page.subhead}
          </p>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-stone-800 mb-8">{page.introTitle}</h2>

            {/* Contact details */}
            <div className="flex flex-wrap justify-center gap-6 text-sm text-stone-500 mb-12">
              <a href={`tel:${page.phone.replace(/\s+/g, '')}`} className="hover:text-orange-600 transition-colors flex items-center gap-2">
                <Phone className="w-4 h-4 text-orange-600" />
                {page.phone}
              </a>
              <a href={`mailto:${page.email}`} className="hover:text-orange-600 transition-colors flex items-center gap-2">
                <Mail className="w-4 h-4 text-orange-600" />
                {page.email}
              </a>
            </div>
          </div>

          {/* Filter */}
          {categories.length > 1 && (
            <div className="flex flex-wrap justify-center gap-3 mb-12">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => handleFilterChange(cat)}
                  className={cn(
                    "px-6 py-2.5 rounded-full text-sm font-bold transition-all border",
                    filter === cat 
                      ? "bg-orange-600 border-orange-600 text-white shadow-lg shadow-orange-900/20" 
                      : "bg-white border-stone-200 text-stone-600 hover:border-orange-600 hover:text-orange-600"
                  )}
                >
                  {cat}
                </button>
              ))}
            </div>
          )}

          {/* Grid */}
          {mappedImages.length === 0 ? (
            <div className="text-center py-16 bg-stone-50 rounded-2xl border border-stone-100 p-8 max-w-2xl mx-auto">
              <div className="w-16 h-16 bg-orange-50 text-orange-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="fa-light fa-images text-2xl" />
              </div>
              <h3 className="text-xl font-bold text-stone-800 mb-2">Galleriet opdateres</h3>
              <p className="text-stone-600">
                Vi er i gang med at opdatere vores billedgalleri med nye, spændende projekter. 
                Du kan i mellemtiden se billeder af vores arbejde på de enkelte ydelsessider.
              </p>
            </div>
          ) : (
            <div id="gallery-section" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 scroll-mt-24">
              {currentItems.map((item, idx) => (
                <div 
                  key={idx}
                  className="group relative aspect-square overflow-hidden rounded-2xl bg-stone-100 cursor-pointer shadow-md hover:shadow-xl transition-all duration-300"
                  onClick={() => setSelectedImage(item)}
                >
                  <img 
                    src={getOptimizedImageUrl(item.src, 600)} 
                    alt={item.alt}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-stone-900/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col items-center justify-center gap-2">
                    <div className="bg-white/20 backdrop-blur-md p-3 rounded-full border border-white/30">
                      <Maximize2 className="w-6 h-6 text-white" />
                    </div>
                    {item.alt && (item.alt.toLowerCase() === 'tv-inspektion' || item.alt.toLowerCase() === 'kloakseparering') && (
                      <span className="text-white text-xs font-semibold px-3 py-1 bg-stone-950/60 backdrop-blur-sm rounded-full max-w-[80%] text-center truncate">
                        {item.alt}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="mt-16 flex justify-center items-center gap-2">
              <button
                onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className={cn(
                  "p-3 rounded-xl border border-stone-200 text-stone-600 hover:border-orange-600 hover:text-orange-600 transition-all disabled:opacity-40 disabled:hover:border-stone-200 disabled:hover:text-stone-600 cursor-pointer disabled:cursor-not-allowed"
                )}
                aria-label="Forrige side"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>

              <div className="flex items-center gap-1.5">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={cn(
                      "w-11 h-11 rounded-xl text-sm font-bold transition-all border cursor-pointer",
                      currentPage === pageNum
                        ? "bg-orange-600 border-orange-600 text-white shadow-md shadow-orange-900/10"
                        : "bg-white border-stone-200 text-stone-600 hover:border-orange-600 hover:text-orange-600"
                    )}
                  >
                    {pageNum}
                  </button>
                ))}
              </div>

              <button
                onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className={cn(
                  "p-3 rounded-xl border border-stone-200 text-stone-600 hover:border-orange-600 hover:text-orange-600 transition-all disabled:opacity-40 disabled:hover:border-stone-200 disabled:hover:text-stone-600 cursor-pointer disabled:cursor-not-allowed"
                )}
                aria-label="Næste side"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-[100] bg-stone-900/95 flex items-center justify-center p-4 md:p-12"
          onClick={() => setSelectedImage(null)}
        >
          <button className="absolute top-8 right-8 text-white hover:text-orange-500 transition-colors">
            <X className="w-8 h-8" />
          </button>
          <div className="max-w-5xl w-full h-full flex flex-col items-center justify-center gap-6" onClick={e => e.stopPropagation()}>
            <img 
              src={getOptimizedImageUrl(selectedImage.src, 1600)} 
              alt={selectedImage.alt}
              className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl"
            />
          </div>
        </div>
      )}
    </>
  );
}
